import { useState, useEffect, useRef } from "react";
import { Package, Trash2, Loader2, Plus, Search, X, ClipboardList, Link2, FileSignature, Download, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import ConfirmModal from "./ConfirmModal";
import { toast } from "./ui/sonner";

/**
 * Materials block for JobCardDetail — Iter 290zm.
 *
 * Hard rules:
 *  • Picking is the SINGLE source of truth for materials. This block
 *    fetches /api/jobcards/{jid}/materials which returns picking rows
 *    where source_jobcard_id == this_jc, across ALL picking lists
 *    linked to the JC.
 *  • Adding a material here CREATES A PICKING ROW (auto-creates a
 *    picking list if none is linked yet — backend handles that).
 *  • Each material row shows the source picking-list chip and links
 *    through to the picking page (where qty editing happens).
 *  • Removing a row UNASSIGNS it from this JC (clears source_jobcard_id)
 *    — it stays on the picking list because the warehouse may still
 *    need to pull it for someone else.
 *  • Prices are NEVER displayed.
 */
export default function JobCardMaterialsBlock({ job, canEdit, onChanged, user }) {
  // Iter 290zm — fetch materials live from picking source. We can't trust
  // job.materials anymore (it may contain only legacy rows pre-migration).
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!job?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/jobcards/${job.id}/materials`);
      setMaterials(data?.materials || []);
    } catch {
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [job?.id, job?.picking_list_ids?.length, job?.materials?.length]);

  const removeRow = async () => {
    if (!confirmId) return;
    setBusy(true);
    try {
      await api.delete(`/jobcards/${job.id}/materials/${confirmId}`);
      setConfirmId(null);
      onChanged?.();
      await load();
      toast.success("Row unassigned from this job card.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed.");
    } finally {
      setBusy(false);
    }
  };

  const onMaterialAdded = async () => {
    setAdding(false);
    onChanged?.();
    await load();
    toast.success("Material added to picking list.");
  };

  return (
    <div className="hv-card p-4" data-testid="jobcard-materials-block">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h4 className="font-display font-bold flex items-center gap-2">
          <Package className="w-4 h-4 text-cyan-300" />
          Materials
          <span className="text-xs text-[#647087] font-normal">
            ({loading ? "…" : materials.length})
          </span>
        </h4>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAdding((v) => !v)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="jc-material-add-btn"
            >
              <Plus className="w-3 h-3" />
              {adding ? "Cancel" : "Add material"}
            </button>
          </div>
        )}
      </div>

      {adding && (
        <AddMaterialPanel
          job={job}
          onAdded={onMaterialAdded}
          onCancel={() => setAdding(false)}
        />
      )}

      {/* Iter 395 — Pull-from-Sales-Order strip. When the JC has one or
          more Sales Orders linked (via jc.sales_order_ids), show a chip
          per SO with a one-click "Pull items into picking slip" button.
          This closes the gap where techs had no way to convert an SO
          into a picking slip from inside the JC. */}
      {canEdit && (
        <PullFromSalesOrderStrip
          job={job}
          onPulled={async () => { await load(); onChanged?.(); }}
        />
      )}

      {!loading && materials.length === 0 ? (
        <div className="text-xs text-[#647087] italic py-4 text-center" data-testid="jc-materials-empty">
          {canEdit ? "No materials yet. Tap “Add material” to push a row onto a picking list." : "No materials yet."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs hv-table-keep" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#647087]">
                <th className="text-left py-1 pr-2">Item</th>
                <th style={{ width: 56 }} className="text-right py-1 px-2">Qty</th>
                <th style={{ width: 60 }} className="text-left py-1 px-2 hidden sm:table-cell">Unit</th>
                <th style={{ width: 140 }} className="text-left py-1 px-2 hidden sm:table-cell">Picking list</th>
                {canEdit && <th style={{ width: 32 }} className="py-1"></th>}
              </tr>
            </thead>
            <tbody>
              {materials.map((m, i) => (
                <tr key={m.id || i} className="border-t border-white/5" data-testid={`material-row-${m.id || i}`}>
                  <td className="py-1.5 pr-2">
                    <div className="text-white truncate" title={m.name}>{m.name}</div>
                    {m.sku && <div className="text-[10px] text-[#647087] font-mono truncate">{m.sku}</div>}
                    {m.notes && <div className="text-[10px] text-[#a0aabf] italic truncate" title={m.notes}>{m.notes}</div>}
                    {/* Iter 427.p28 — Duplicate-guard chip. When the same
                        SO line was pulled into multiple picking lists
                        that both link to this JC, the backend now
                        collapses them to a single row and lists the
                        other lists in `also_on` so we can WARN the
                        picker that this item is federated — without
                        the chip, they would pick twice. */}
                    {Array.isArray(m.also_on) && m.also_on.length > 0 && (
                      <div
                        className="mt-0.5 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-amber-400/40 bg-amber-500/10 text-amber-100"
                        data-testid={`material-alsoon-${m.id}`}
                        title={`Also on: ${m.also_on.join(", ")}. Deduped to avoid double-picking.`}
                      >
                        <AlertTriangle className="w-2.5 h-2.5" />
                        Also on {m.also_on.length} other list{m.also_on.length === 1 ? "" : "s"} · deduped
                      </div>
                    )}
                    {/* Mobile: show picking list chip inline since col is hidden on mobile */}
                    {m.picking_list_id && (
                      <Link
                        to={`/picking/lists/${m.picking_list_id}`}
                        className="sm:hidden inline-flex items-center gap-1 mt-0.5 text-[10px] text-cyan-300 hover:text-cyan-200 font-mono"
                        data-testid={`material-pickinglist-mobile-${m.id}`}
                      >
                        <Link2 className="w-2.5 h-2.5" />
                        {m.picking_list_name || m.picking_list_short_id || "Picking list"}
                      </Link>
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono tabular-nums">{m.quantity}</td>
                  <td className="py-1.5 px-2 text-[#a0aabf] hidden sm:table-cell">{m.unit || "pcs"}</td>
                  <td className="py-1.5 px-2 hidden sm:table-cell">
                    {m.picking_list_id ? (
                      <Link
                        to={`/picking/lists/${m.picking_list_id}`}
                        className="inline-flex items-center gap-1 text-[10px] text-cyan-300 hover:text-cyan-200 truncate max-w-full"
                        data-testid={`material-pickinglist-${m.id}`}
                        title={m.picking_list_name}
                      >
                        <Link2 className="w-3 h-3 shrink-0" />
                        <span className="truncate">{m.picking_list_name || m.picking_list_short_id || "Open"}</span>
                      </Link>
                    ) : (
                      <span className="text-[9px] uppercase tracking-wider text-[#647087]">
                        {m.source || "manual"}
                      </span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="py-1.5 pl-1 text-right">
                      <button
                        type="button"
                        onClick={() => setConfirmId(m.id)}
                        disabled={busy}
                        className="text-rose-300/70 hover:text-rose-300 p-1"
                        data-testid={`material-delete-${m.id}`}
                        title={m.picking_item_id ? "Unassign from this job card (row stays on picking list)" : "Remove"}
                      >
                        {busy && confirmId === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={confirmId != null}
        onClose={() => setConfirmId(null)}
        onConfirm={removeRow}
        title="Unassign this material?"
        confirmLabel="Unassign"
        message="The row will be removed from this job card but stays on its picking list (so the warehouse can still pull or reassign it)."
        testId="material-delete-confirm"
      />
    </div>
  );
}

/**
 * AddMaterialPanel — inline type-ahead using /api/inventory/resolve.
 * Iter 290zm — when the JC has 2+ linked picking lists, prompt the
 * user which one to add the row to. (0 → backend auto-creates; 1 →
 * silent default; 2+ → 409 + options → inline picker.)
 */
function AddMaterialPanel({ job, onAdded, onCancel }) {
  const jobId = job?.id;
  const [text, setText] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [picked, setPicked] = useState(null);  // {inventory_id, name, sku, unit}
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNonStock, setShowNonStock] = useState(false);
  const [listOptions, setListOptions] = useState(null);   // [{id,name,short_id}] when 2+ picking lists
  const [chosenListId, setChosenListId] = useState("");
  const debounce = useRef(null);

  useEffect(() => {
    if (!text.trim() || picked) {
      setCandidates([]);
      return;
    }
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.post("/inventory/resolve", { text: text.trim(), limit: 8 });
        setCandidates(data.candidates || []);
      } catch (_e) {
        setCandidates([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [text, picked]);

  const pick = (c) => {
    setPicked(c);
    setText(c.name);
    setCandidates([]);
  };

  const reset = () => {
    setText(""); setPicked(null); setQty(1); setNotes("");
    setShowNonStock(false); setListOptions(null); setChosenListId("");
  };

  const buildPayload = () => (
    picked
      ? {
          inventory_id: picked.inventory_id,
          name: picked.name,
          sku: picked.sku,
          quantity: Number(qty),
          unit: picked.unit || "pcs",
          notes: notes || "",
        }
      : {
          name: text.trim(),
          quantity: Number(qty),
          unit: "pcs",
          notes: notes || "non-stock — no inventory match",
        }
  );

  const submit = async (forcedListId) => {
    if (!text.trim() && !picked) {
      toast.error("Type a description or pick an inventory item.");
      return;
    }
    if (Number(qty) <= 0) {
      toast.error("Quantity must be positive.");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (forcedListId) payload.picking_list_id = forcedListId;
      await api.post(`/jobcards/${jobId}/materials`, payload);
      reset();
      onAdded?.();
    } catch (e) {
      // 409 = picking_list_choice_required → render the inline picker
      if (e?.response?.status === 409 && e.response?.data?.detail?.code === "picking_list_choice_required") {
        setListOptions(e.response.data.detail.options || []);
        toast.message?.("Pick which picking list to push this row onto.");
      } else {
        toast.error(e?.response?.data?.detail || e?.response?.data?.detail?.message || "Add failed.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mb-3 rounded-md border border-cyan-400/30 bg-cyan-500/[0.04] p-3 space-y-2"
      data-testid="jc-add-material-panel"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-300 flex items-center gap-1.5">
          <Plus className="w-3 h-3" />
          Add material
        </div>
        <button type="button" onClick={onCancel} className="text-[#647087] hover:text-white" aria-label="Close add material panel">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#647087]" />
        <input
          type="text"
          value={text}
          onChange={(e) => { setText(e.target.value); if (picked) setPicked(null); }}
          placeholder="Type an item — e.g. “3-phase esybox max” or “P*UV55W”"
          className="hv-input hv-input-search text-xs"
          data-testid="jc-add-material-search"
          autoFocus
        />
        {searching && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-[#647087]" />}
      </div>

      {candidates.length > 0 && (
        <div className="border border-white/10 rounded-md bg-[#0a0b0e] divide-y divide-white/5 max-h-56 overflow-auto" data-testid="jc-add-material-candidates">
          {candidates.map((c) => (
            <button
              key={c.inventory_id}
              type="button"
              onClick={() => pick(c)}
              className="w-full text-left px-3 py-2 hover:bg-white/[0.04] flex items-center gap-2"
              data-testid={`jc-add-material-candidate-${c.inventory_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white truncate">{c.name}</div>
                <div className="flex items-center gap-2 text-[9px] text-[#647087] mt-0.5">
                  {c.sku && <span className="font-mono">{c.sku}</span>}
                  {c.manufacturer && <span>· {c.manufacturer}</span>}
                  <span>· on-hand {c.on_hand ?? 0}</span>
                </div>
              </div>
              <span
                className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${
                  c.score >= 0.75
                    ? "border-emerald-400/40 text-emerald-300 bg-emerald-500/10"
                    : c.score >= 0.45
                      ? "border-amber-400/40 text-amber-300 bg-amber-500/10"
                      : "border-rose-400/30 text-rose-300 bg-rose-500/10"
                }`}
                title={`Match reason: ${c.reason}`}
              >
                {(c.score * 100).toFixed(0)}%
              </span>
            </button>
          ))}
        </div>
      )}

      {text.trim() && !picked && candidates.length === 0 && !searching && (
        <div className="text-[10px] text-amber-200/80 bg-amber-400/10 border border-amber-400/30 rounded px-2 py-1 flex items-center justify-between gap-2">
          <span>No inventory match. Log as <b>non-stock</b>?</span>
          <button
            type="button"
            onClick={() => setShowNonStock(true)}
            className="text-[10px] underline hover:text-amber-100"
            data-testid="jc-add-material-non-stock"
          >
            Yes, log as non-stock
          </button>
        </div>
      )}

      {(picked || showNonStock) && (
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-1">
            <label className="text-[9px] uppercase tracking-wider text-[#647087] block mb-1">Qty</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="hv-input text-xs"
              data-testid="jc-add-material-qty"
            />
          </div>
          <div className="col-span-2">
            <label className="text-[9px] uppercase tracking-wider text-[#647087] block mb-1">Note (optional)</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Replaced cracked union under sink"
              className="hv-input text-xs"
              data-testid="jc-add-material-note"
            />
          </div>
        </div>
      )}

      {/* Iter 290zm — inline picking-list picker when JC has 2+ linked lists. */}
      {listOptions && (
        <div className="rounded border border-amber-400/30 bg-amber-500/[0.06] p-2 space-y-1" data-testid="jc-add-material-list-picker">
          <div className="text-[10px] uppercase tracking-wider font-bold text-amber-300">
            This job card is linked to multiple picking lists. Pick one:
          </div>
          {listOptions.map((lo) => (
            <button
              key={lo.id}
              type="button"
              onClick={() => { setChosenListId(lo.id); submit(lo.id); }}
              disabled={saving}
              className="w-full text-left px-2 py-1.5 rounded border border-white/5 bg-[#0a0b0e] hover:bg-white/[0.04] text-[11px] flex items-center gap-2 disabled:opacity-50"
              data-testid={`jc-add-material-list-${lo.id}`}
            >
              <ClipboardList className="w-3 h-3 text-cyan-300 shrink-0" />
              <span className="text-white truncate flex-1">{lo.name || "(unnamed list)"}</span>
              {lo.short_id && <span className="text-[10px] font-mono text-[#a0aabf] shrink-0">{lo.short_id}</span>}
            </button>
          ))}
        </div>
      )}

      {(picked || showNonStock) && !listOptions && (
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={reset} className="hv-btn hv-btn-ghost text-xs" data-testid="jc-add-material-reset">
            Clear
          </button>
          <button
            type="button"
            onClick={() => submit()}
            disabled={saving}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid="jc-add-material-submit"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Add to picking
          </button>
        </div>
      )}
    </div>
  );
}


/**
 * PullFromSalesOrderStrip — Iter 395.
 *
 * When a job card has one or more sales orders linked (via
 * jc.sales_order_ids), render a chip per SO with a "Pull items"
 * button. On click:
 *
 *   • JC has 0 linked picking lists    → POST /picking/from-sales-order
 *     (creates a fresh list + auto-links to this JC via cross-link).
 *   • JC has 1 linked picking list     → POST /picking/lists/{lid}/pull-from-sales-doc
 *   • JC has 2+ linked picking lists   → inline picker (mirrors the
 *     add-material multi-list picker so the UX is consistent).
 *
 * All backend endpoints are idempotent — clicking twice accumulates
 * qty rather than double-inserting.
 */
function PullFromSalesOrderStrip({ job, onPulled }) {
  const [salesOrders, setSalesOrders] = useState([]);
  const [pickingLists, setPickingLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pullingId, setPullingId] = useState(null);
  const [pickerFor, setPickerFor] = useState(null); // sales_doc_id when multi-list picker is open

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!job?.id) return;
      setLoading(true);
      try {
        const { data } = await api.get(`/entity-links/jobcard/${job.id}`);
        if (cancelled) return;
        setSalesOrders(data?.sales_doc || []);
        setPickingLists(data?.picking || []);
      } catch {
        if (!cancelled) { setSalesOrders([]); setPickingLists([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [job?.id, job?.sales_order_ids?.length, job?.picking_list_ids?.length]);

  if (loading || salesOrders.length === 0) return null;

  const pull = async (so, forcedListId, opts = {}) => {
    setPullingId(so.id);
    try {
      // Iter 410.4 · Refresh the entity-links right before dispatching
      // to avoid the race where the local `pickingLists` state is
      // stale (e.g. user clicked twice while the first request was
      // in-flight). Otherwise the branch below sees `lists.length===0`
      // and creates a duplicate picking slip.
      let lists = pickingLists;
      try {
        const fresh = await api.get(`/entity-links/jobcard/${job.id}`);
        lists = fresh?.data?.picking || pickingLists;
        setPickingLists(lists);
      } catch { /* fall back to state */ }

      // Iter 427.p29 — Prevent-at-write-time guard wrapper. Backend
      // returns 409 with a `conflict` payload when pulling this SO
      // would cause double picking. We surface it to the tech with a
      // clear confirm dialog before retrying with force=true.
      const attempt = async (url) => {
        try {
          return await api.post(url, { sales_doc_id: so.id, force: !!opts.force });
        } catch (err) {
          const detail = err?.response?.data?.detail;
          const conflict = detail?.conflict;
          if (err?.response?.status === 409 && conflict) {
            const other = (conflict.conflicts || [])
              .map((c) => c.list_short_id || c.list_name || c.list_id).join(", ");
            const msg = (
              `⚠ Double-picking risk\n\n${detail.message}\n\n`
              + `Also on: ${other}\n\n`
              + "Pull anyway? (only do this if the tech WILL pick this SO twice on purpose)"
            );
            if (window.confirm(msg)) {
              return await api.post(url, { sales_doc_id: so.id, force: true });
            }
            throw new Error("__user_cancelled_dupe__");
          }
          throw err;
        }
      };

      if (forcedListId) {
        const { data } = await attempt(`/picking/lists/${forcedListId}/pull-from-sales-doc`);
        toast.success(`Pulled ${data?.added ?? 0} row${data?.added === 1 ? "" : "s"} from ${so.short_id || "SO"} into picking slip.`);
      } else if (lists.length === 0) {
        // No picking list yet — create fresh from the SO. Cross-link
        // to this JC happens server-side via _auto_link_jobcards_via_so.
        const { data } = await attempt(`/picking/from-sales-order`);
        toast.success(`Created picking slip · ${data?.items_created ?? 0} rows from ${so.short_id || "SO"}.`);
      } else if (lists.length === 1) {
        const { data } = await attempt(`/picking/lists/${lists[0].id}/pull-from-sales-doc`);
        toast.success(`Pulled ${data?.added ?? 0} row${data?.added === 1 ? "" : "s"} into ${lists[0].__label || "picking slip"}.`);
      } else {
        // Multiple lists — open the inline picker.
        setPickerFor(so.id);
        return;
      }
      setPickerFor(null);
      await onPulled?.();
    } catch (e) {
      if (e?.message === "__user_cancelled_dupe__") {
        toast.message("Pull cancelled — no double picking risk taken.");
      } else {
        toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Pull failed.");
      }
    } finally {
      setPullingId(null);
    }
  };

  return (
    <div
      className="mb-3 rounded-md border border-emerald-400/25 bg-emerald-500/[0.04] p-3 space-y-2"
      data-testid="jc-pull-from-so-strip"
    >
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300 flex items-center gap-1.5">
        <FileSignature className="w-3 h-3" />
        Sales orders on this job card
        <span className="text-[#647087] font-normal ml-1">({salesOrders.length})</span>
      </div>
      <ul className="space-y-1.5">
        {salesOrders.map((so) => {
          const isPulling = pullingId === so.id;
          const showPicker = pickerFor === so.id && pickingLists.length >= 2;
          return (
            <li key={so.id} data-testid={`jc-pull-so-row-${so.id}`}>
              <div className="flex items-center gap-2 p-2 rounded border border-white/5 bg-white/[0.02]">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                    {so.short_id && (
                      <span
                        className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border border-emerald-400/40 text-emerald-300 bg-emerald-500/10"
                      >
                        {so.short_id}
                      </span>
                    )}
                    {so.__status && (
                      <span className="text-[9px] uppercase tracking-wider text-[#647087] font-mono">
                        {String(so.__status).replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                  <Link
                    to={`/sales-documents/${so.id}/edit`}
                    className="text-xs text-white hover:text-emerald-300 truncate block"
                    data-testid={`jc-pull-so-open-${so.id}`}
                    title="Open sales order"
                  >
                    {so.__label || so.name || "Sales order"}
                  </Link>
                </div>
                <button
                  type="button"
                  onClick={() => pull(so)}
                  disabled={isPulling}
                  className="hv-btn hv-btn-primary text-[11px] disabled:opacity-50 shrink-0"
                  data-testid={`jc-pull-so-btn-${so.id}`}
                  title="Push items from this sales order into the picking slip"
                >
                  {isPulling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                  {isPulling ? "Pulling…" : "Pull items"}
                </button>
              </div>
              {showPicker && (
                <div className="mt-1 ml-2 pl-3 border-l-2 border-emerald-400/30 space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">
                    Which picking slip?
                  </div>
                  {pickingLists.map((pl) => (
                    <button
                      key={pl.id}
                      type="button"
                      onClick={() => pull(so, pl.id)}
                      disabled={isPulling}
                      className="w-full text-left px-2 py-1.5 rounded border border-white/5 bg-[#0a0b0e] hover:bg-white/[0.04] text-[11px] flex items-center gap-2 disabled:opacity-50"
                      data-testid={`jc-pull-so-list-${so.id}-${pl.id}`}
                    >
                      <ClipboardList className="w-3 h-3 text-cyan-300 shrink-0" />
                      <span className="text-white truncate flex-1">{pl.__label || "(unnamed list)"}</span>
                      {pl.short_id && <span className="text-[10px] font-mono text-[#a0aabf] shrink-0">{pl.short_id}</span>}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPickerFor(null)}
                    className="text-[10px] text-[#647087] hover:text-white underline"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
